// One option in a stacked list of choices, as a full-width card. The
// selected tone is the host's call: `soil` is the form renderer's dark
// card; `daisy` is the light highlight for lists that sit beside
// illustrations or on a long run of screens. Only colors transition —
// never outline, which would flash the browser's focus ring on the
// first click.

import type { ReactNode } from "react";

export type ChoiceTone = "soil" | "daisy";
export type ChoiceSize = "base" | "lg";

const BASE =
  "block w-full rounded-xl border px-5 py-4 text-left font-medium transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cantelope/60";

const SELECTED: Record<ChoiceTone, string> = {
  soil: "border-soil bg-soil text-white",
  daisy: "border-daisy-400 bg-daisy-100 text-soil",
};

const IDLE: Record<ChoiceTone, string> = {
  soil: "border-oat-200 bg-white text-soil enabled:hover:border-soil",
  daisy: "border-oat-200 bg-white text-soil enabled:hover:border-daisy-400",
};

export function choiceCardClass(
  selected: boolean,
  tone: ChoiceTone,
  size: ChoiceSize,
): string {
  const text = size === "lg" ? "text-lg" : "";
  return `${BASE} ${text} ${selected ? SELECTED[tone] : IDLE[tone]}`.trim();
}

export default function ChoiceCard({
  selected,
  onSelect,
  tone = "soil",
  size = "base",
  asRadio = false,
  centered = false,
  tabIndex,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  tone?: ChoiceTone;
  size?: ChoiceSize;
  /** Announces the card as a radio of an enclosing radiogroup instead of
   * a toggle button. */
  asRadio?: boolean;
  /** Centers the label, for a row of short answers side by side. */
  centered?: boolean;
  tabIndex?: number;
  children: ReactNode;
}) {
  const selection = asRadio
    ? ({ role: "radio", "aria-checked": selected } as const)
    : ({ "aria-pressed": selected } as const);
  return (
    <button
      type="button"
      className={
        centered
          ? choiceCardClass(selected, tone, size).replace(
              "text-left",
              "text-center",
            )
          : choiceCardClass(selected, tone, size)
      }
      {...selection}
      tabIndex={tabIndex}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}
